/*
Usage: 
  <skio-cart-upsell item='{{ item | json | escape }}' line='{{ forloop.index }}'></skio-cart-upsell>
  <script src="{{ 'skio-cart-upsell.js' | asset_url }}" type="module"></script>
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js'

const skioCartStyles = css`
  /*
   * SPACEMILK skin — Figma 1265:10206.
   *
   * Replaces Skio's stock navy/teal button. Skio confirmed 2026-09-09 that this
   * file is the supported place to restyle the element, since LitElement puts
   * everything in a shadow root and section-cart-drawer.css cannot reach in.
   *
   * Custom properties DO cross the shadow boundary, so the two brand faces are
   * pulled from the theme's own tokens rather than restated — the panel follows
   * a font change in custom-fonts.liquid for free. Values are px because the
   * whole cart drawer stylesheet is px.
   */
  :host {
    display: block;
  }

  :host([hidden]) {
    display: none;
  }

  .smk-sub {
    position: relative;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    border-radius: 12px;
    background-color: rgba(241, 235, 156, 0.5);
    color: #2d2926;
  }

  .smk-sub__body {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .smk-sub__heading {
    margin: 0;
    font-family: var(--font-display-family, sans-serif);
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  .smk-sub__benefits {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .smk-sub__benefits li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-brand-family, sans-serif);
    font-size: 12px;
    line-height: 16px;
  }

  .smk-sub__benefits svg {
    flex: none;
    width: 16px;
    height: 16px;
  }

  /* Same button as the drawer's checkout and the PDP's add — 2px black ring,
     lumen yellow on space black. Height is fixed, so the frame's asymmetric
     21/19 padding is moot and the label is flex-centred instead. */
  .smk-sub__cta {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 46px;
    padding: 0 28px;
    border: 2px solid #000;
    border-radius: 50px;
    background-color: #2d2926;
    color: #f1eb9c;
    font-family: var(--font-display-family, sans-serif);
    font-size: 16px;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 0.72px;
    text-transform: uppercase;
    cursor: pointer;
  }

  .smk-sub__cta:disabled {
    opacity: 0.5;
    cursor: default;
  }

  /* 1265:10227 hangs 13px above the panel. Right-anchored rather than the
     frame's left: 303 — that number is only true at the frame's fixed 412, and
     the label is a merchant setting whose width can change. */
  .smk-sub__badge {
    position: absolute;
    top: -13px;
    right: 16px;
    padding: 8px 12px 4px;
    border: 1px solid #2d2926;
    border-radius: 20px;
    background-color: #f1eb9c;
    color: #2d2926;
    font-family: var(--font-display-family, sans-serif);
    font-size: 11px;
    font-weight: 700;
    line-height: 13.2px;
    text-transform: uppercase;
    white-space: nowrap;
  }
`;

export class SkioCartUpsell extends LitElement {
  static styles = skioCartStyles;

  static properties = {
    item: { type: Object },
    line: { type: Number },

    product: { type: Object },
    selectedVariant: { type: Object },

    skioSellingPlanGroups: {},
    availableSellingPlanGroups: {},

    selectedSellingPlanGroup: {},
    selectedSellingPlan: {},

    moneyFormatter: {},
    currency: { type: String }
  };

  constructor() {
    super();

    this.item = null;
    this.line = null;

    this.product = null;
    this.selectedVariant = null;

    this.skioSellingPlanGroups = [];
    this.availableSellingPlanGroups = [];

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.currency = 'USD';

    this.moneyFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency,
    });
  }

  render() {
    /*
     * SPACEMILK: `hidden` is toggled here, not in CSS. The light DOM of this
     * element is always empty — everything it draws lives in the shadow root —
     * so `:empty` in section-cart-drawer.css can never tell "nothing to offer"
     * from "panel drawn", and an empty flex child would still take a row and a
     * 20px gap in the line item.
     *
     * The frame shows the panel on ONE-TIME lines only: an already-subscribed
     * line states its cadence in the row's meta and gets no panel. That is why
     * changeFrequency() below is no longer reached — it is kept, unused, so a
     * later Skio update still merges and re-enabling it is one line.
     */
    const offerable = !!(
      this.item &&
      this.product &&
      this.selectedVariant &&
      !this.selectedSellingPlan &&
      this.availableSellingPlanGroups.length
    );

    this.hidden = !offerable;

    return offerable ? this.upgradePanel() : html``;
  }

  /* Read-at-render copy, passed down from the Liquid snippet so the strings stay
     merchant-editable in Theme settings > Cart. */
  copy(name, fallback) {
    const value = this.getAttribute('data-' + name);
    return value === null || value === '' ? fallback : value;
  }

  /*
   * The frame hardcodes "an Extra 15%". The real saving is whatever Skio put on
   * the plan — 9% on this catalogue — so it is computed from the allocation and
   * substituted into the merchant's string through a [discount] token, the same
   * convention Skio's own discount_text setting uses. If the plan carries no
   * comparison price the token resolves to nothing rather than a wrong number.
   */
  discountPercent() {
    const plan = this.availableSellingPlanGroups?.[0]?.selling_plans?.[0];
    if (!plan) return null;

    const allocation = (this.selectedVariant?.selling_plan_allocations || []).find(
      (a) => Number(a.selling_plan_id) === Number(plan.id)
    );
    if (!allocation) return null;

    const full = allocation.compare_at_price;
    if (!full || full <= allocation.price) return null;

    return Math.round((1 - allocation.price / full) * 100);
  }

  upgradePanel = () => {
    const percent = this.discountPercent();
    const heading = this.copy('heading', 'Subscribe and save')
      .replace('[discount]', percent === null ? '' : percent + '%')
      .replace(/\s+/g, ' ')
      .trim();

    const benefits = ['benefit-1', 'benefit-2', 'benefit-3']
      .map((key) => this.copy(key, ''))
      .filter(Boolean);

    const badge = this.copy('badge', '');

    return html`
      <div class="smk-sub">
        ${badge ? html`<span class="smk-sub__badge">${badge}</span>` : ''}

        <div class="smk-sub__body">
          <p class="smk-sub__heading">${heading}</p>

          ${benefits.length
            ? html`
                <ul class="smk-sub__benefits">
                  ${benefits.map(
                    (benefit) => html`
                      <li>
                        ${this.truckIcon()}
                        <span>${benefit}</span>
                      </li>
                    `
                  )}
                </ul>
              `
            : ''}
        </div>

        <button
          class="smk-sub__cta"
          type="button"
          @click=${() =>
            this.selectSellingPlan(
              this.availableSellingPlanGroups[0].selling_plans[0].id
            )}
        >
          ${this.copy('cta', 'Upgrade and save')}
        </button>
      </div>
    `;
  };

  /* 1265:10212. Same two paths as the theme's assets/icon-truck-line.svg, which
     is the identical glyph — inlined rather than fetched because a shadow root
     cannot reach an inline_asset_content in the page. The frame repeats this one
     icon on all three rows. */
  truckIcon() {
    return html`
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path
          fill="currentColor"
          d="M8.965 18C8.84612 18.8343 8.43022 19.5977 7.79368 20.1499C7.15714 20.7022 6.34272 21.0063 5.5 21.0063C4.65728 21.0063 3.84286 20.7022 3.20632 20.1499C2.56979 19.5977 2.15388 18.8343 2.035 18H1.2C1.08954 18 1 17.9105 1 17.8V6C1 5.73478 1.10536 5.48043 1.29289 5.29289C1.48043 5.10536 1.73478 5 2 5H16C16.2652 5 16.5196 5.10536 16.7071 5.29289C16.8946 5.48043 17 5.73478 17 6V8H19.8992C19.9626 8 20.0223 8.03008 20.06 8.08107L22.9608 12.003C22.9863 12.0374 23 12.0791 23 12.1219V17.8C23 17.9105 22.9105 18 22.8 18H20.965C20.8461 18.8343 20.4302 19.5977 19.7937 20.1499C19.1571 20.7022 18.3427 21.0063 17.5 21.0063C16.6573 21.0063 15.8429 20.7022 15.2063 20.1499C14.5698 19.5977 14.1539 18.8343 14.035 18H8.965ZM15 7H3V15.05C3.39456 14.6472 3.8806 14.3457 4.41675 14.1711C4.9529 13.9966 5.52329 13.9541 6.07938 14.0474C6.63546 14.1407 7.16077 14.3669 7.61061 14.7069C8.06045 15.0469 8.42149 15.4905 8.663 16H14.337C14.505 15.647 14.73 15.326 15 15.05V7ZM17 13H21V12.715L18.992 10H17V13ZM17.5 19C17.898 19 18.2796 18.8419 18.561 18.5605C18.8424 18.2791 19.0005 17.8975 19.0005 17.4995C19.0005 17.1015 18.8424 16.7199 18.561 16.4385C18.2796 16.1571 17.898 15.999 17.5 15.999C17.102 15.999 16.7204 16.1571 16.439 16.4385C16.1576 16.7199 15.9995 17.1015 15.9995 17.4995C15.9995 17.8975 16.1576 18.2791 16.439 18.5605C16.7204 18.8419 17.102 19 17.5 19ZM7 17.5C7 17.303 6.9612 17.108 6.88582 16.926C6.81044 16.744 6.69995 16.5786 6.56066 16.4393C6.42137 16.3001 6.25601 16.1896 6.07403 16.1142C5.89204 16.0388 5.69698 16 5.5 16C5.30302 16 5.10796 16.0388 4.92598 16.1142C4.74399 16.1896 4.57863 16.3001 4.43934 16.4393C4.30005 16.5786 4.18956 16.744 4.11418 16.926C4.0388 17.108 4 17.303 4 17.5C4 17.8978 4.15804 18.2794 4.43934 18.5607C4.72064 18.842 5.10218 19 5.5 19C5.89783 19 6.27936 18.842 6.56066 18.5607C6.84197 18.2794 7 17.8978 7 17.5Z"
        />
        <path fill="currentColor" d="M3 13H15V15V17L8 16.5L3 15V13Z" />
      </svg>
    `;
  }

  upgradeButton = () => {
    if (!this.availableSellingPlanGroups.length) return html``;

    return html`
      <button
        class="skio-cart-upgrade"
        type="button"
        @click=${() =>
          this.selectSellingPlan(
            this.availableSellingPlanGroups[0].selling_plans[0].id
          )
        }>
        Upgrade to subscription
      </button>
    `;
  };

  changeFrequency = () => {
    return html`
      <select
        class="skio-cart-upsell"
        @change=${(e) => this.selectSellingPlan(e.target.value)}>

        ${!this.product.requires_selling_plan ? html`
          <optgroup label="One Time Purchase">
            <option value="">One-time</option>
          </optgroup>
        ` : ''}

        ${this.availableSellingPlanGroups.map(group => html`
          <optgroup label="${group.name} Purchase">
            ${group.selling_plans.map(plan => html`
              <option
                value="${plan.id}"
                .selected=${plan.id == this.selectedSellingPlan?.id}>
                ${plan.name}
              </option>
            `)}
          </optgroup>
        `)}
      </select>
    `;
  };

  updated(changed) {

    if (changed.has('item') && this.item) {
      this.fetchProduct(this.item.handle);
    }

    if (changed.has('product') && this.product) {

      this.skioSellingPlanGroups =
        (this.product.selling_plan_groups || []).filter(
          group => group.app_id === 'SKIO'
        );

      this.selectedVariant =
        (this.product.variants || []).find(
          variant => variant.id == this.item.variant_id
        );

      if (this.item.selling_plan_allocation) {
        const id = this.item.selling_plan_allocation.selling_plan.id;

        this.selectedSellingPlanGroup =
          this.skioSellingPlanGroups.find(group =>
            group.selling_plans.some(plan => plan.id == id)
          );

        this.selectedSellingPlan =
          this.selectedSellingPlanGroup?.selling_plans.find(
            plan => plan.id == id
          );
      }
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {

      const allocations =
        this.selectedVariant.selling_plan_allocations || [];

      // this.log('Variant Allocations:', allocations);

      this.availableSellingPlanGroups =
        this.skioSellingPlanGroups
          .map(group => {

            const mappedPlans =
              group.selling_plans.filter(plan =>
                allocations.some(a =>
                  Number(a.selling_plan_id) === Number(plan.id)
                )
              );

            if (!mappedPlans.length) return null;

            return {
              ...group,
              selling_plans: mappedPlans,
              selected_selling_plan: mappedPlans[0]
            };
          })
          .filter(Boolean);

      // this.log('Filtered Groups:', this.availableSellingPlanGroups);
    }

    if (changed.has('selectedSellingPlan') && this.product) {
      this.updateLineItem();
    }
  }

  updateLineItem() {
    if (!this.item) return;

    if (
      this.item.selling_plan_allocation?.selling_plan?.id ==
      this.selectedSellingPlan?.id
    ) return;

    const body = JSON.stringify({
      line: this.line,
      quantity: this.item.quantity,
      selling_plan: this.selectedSellingPlan
        ? this.selectedSellingPlan.id
        : null,

      sections: this.getSectionsToRender().map(s => s.section),
      sections_url: window.location.pathname
    });

    fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body
    })
      .then(res => res.text())
      .then(state => {
        const parsedState = JSON.parse(state);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'selling-plan-change',
          cartData: parsedState,
          variantId: this.item.variant_id
        });
      })
      .catch(console.error);
  }

  selectSellingPlan(id) {

    if (!id) {
      this.selectedSellingPlanGroup = null;
      this.selectedSellingPlan = null;
      return;
    }

    const group =
      this.availableSellingPlanGroups.find(group =>
        group.selling_plans.some(plan => plan.id == id)
      );

    const selling_plan =
      group?.selling_plans.find(plan => plan.id == id);

    if (selling_plan) {
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
    }
    else {
      this.error('Could not find selling plan:', id);
    }
  }

  money(price) {
    return this.moneyFormatter.format(price / 100.0);
  }

  getSectionsToRender() {
    return [
      { section: 'cart-drawer' },
      { section: 'cart-icon-bubble' },
      { section: 'cart-live-region-text' }
    ];
  }

  fetchProduct = async(handle) => {

    let productCache =
      window.sessionStorage.skioCartProductCache
        ? JSON.parse(window.sessionStorage.skioCartProductCache)
        : [];

    let cachedProduct =
      productCache.find(product => product.handle == handle);

    if (cachedProduct) {
      this.product = cachedProduct;
      return;
    }

    await fetch(`/products/${handle}.js`)
      .then(res => res.json())
      .then(response => {

        this.product = response;

        productCache.push(response);

        window.sessionStorage.skioCartProductCache =
          JSON.stringify(productCache);

        this.requestUpdate();
      });
  };

  log(...args) {
    args.unshift('%c[skio cart upsell]', 'color: #8770f2;');
    console.log(...args);
  }

  error(...args) {
    args.unshift('%c[skio cart upsell]', 'color: red;');
    console.error(...args);
  }
}

customElements.define('skio-cart-upsell', SkioCartUpsell);
  